import { useCallback } from 'react';
import { SnapManager } from '../services/SnapManager.js';
import { TransformManager } from '../services/TransformManager.js';
import { calculateRect, calculateLayout } from '../utils.js';
import { resolveNode } from '../utils/responsive.js';

export const useNodeResizing = ({
  resizingRef,
  activeNodes,
  updateNodes,
  canvasScale,
  rootW,
  rootH,
  settings,
  activeBreakpoint = 'default',
  visualBreakpoint = 'default' // Add visualBreakpoint
}) => {
  const startResize = useCallback((e, nodeId, handle, initialRects, nodeSnapshots) => {
    resizingRef.current = {
      clickedId: nodeId,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      initialRects,
      nodeSnapshots
    };
  }, [resizingRef]);

  const updateResize = useCallback((e) => {
    if (!resizingRef.current) return false;

    // Use visualBreakpoint for calculations
    const effectiveVisualBreakpoint = visualBreakpoint || activeBreakpoint;

    const { startX, startY, initialRects, handle, nodeSnapshots } = resizingRef.current;

    let deltaX = (e.clientX - startX) / canvasScale;
    let deltaY = (e.clientY - startY) / canvasScale;

    const snapToGrid = settings.snapToGrid ?? false;
    const gridSize = settings.gridSize || 10;
    const MIN_SIZE = 20;

    const resizedNodeIds = Array.from(initialRects.keys());
    const updatedNodesMap = new Map();

    resizedNodeIds.forEach(id => {
      const rawNode = activeNodes.find(n => n.id === id);
      if (!rawNode) return;

      // We use the initial snapshot for math, but we need resolved anchors for layout calc
      const initialRect = initialRects.get(id);
      if (!initialRect) return;

      let newW = initialRect.width;
      let newH = initialRect.height;
      let newX = initialRect.x;
      let newY = initialRect.y;

      if (e.altKey) {
        // Symmetric Resize
        if (handle.includes('e')) {
          newW = Math.max(MIN_SIZE, initialRect.width + deltaX * 2);
          newX = initialRect.x - (newW - initialRect.width) / 2;
        }
        if (handle.includes('w')) {
          newW = Math.max(MIN_SIZE, initialRect.width - deltaX * 2);
          newX = initialRect.x - (newW - initialRect.width) / 2;
        }
        if (handle.includes('s')) {
          newH = Math.max(MIN_SIZE, initialRect.height + deltaY * 2);
          newY = initialRect.y - (newH - initialRect.height) / 2;
        }
        if (handle.includes('n')) {
          newH = Math.max(MIN_SIZE, initialRect.height - deltaY * 2);
          newY = initialRect.y - (newH - initialRect.height) / 2;
        }
      } else {
        // Normal Resize
        if (handle.includes('e')) newW = Math.max(MIN_SIZE, initialRect.width + deltaX);
        if (handle.includes('s')) newH = Math.max(MIN_SIZE, initialRect.height + deltaY);
        if (handle.includes('w')) {
          const w = Math.max(MIN_SIZE, initialRect.width - deltaX);
          newW = w;
          newX = initialRect.x + (initialRect.width - w);
        }
        if (handle.includes('n')) {
          const h = Math.max(MIN_SIZE, initialRect.height - deltaY);
          newH = h;
          newY = initialRect.y + (initialRect.height - h);
        }
      }

      // Proportional Scaling (Shift)
      if (e.shiftKey && ['ne', 'nw', 'se', 'sw'].includes(handle)) {
          const ratio = initialRect.width / initialRect.height;
          
          // Determine driving axis based on which dimension changed more relative to ratio
          const deltaW = Math.abs(newW - initialRect.width);
          const deltaH = Math.abs(newH - initialRect.height);
          
          // Normalize comparison
          const xDrives = deltaW > deltaH * ratio;
          
          if (xDrives) {
              newH = Math.max(MIN_SIZE, newW / ratio);
              newW = newH * ratio;
          } else {
              newW = Math.max(MIN_SIZE, newH * ratio);
              newH = newW / ratio;
          }
          
          // Re-adjust position based on anchors to keep opposite corner fixed
          if (e.altKey) {
              // Symmetric: Center is fixed
              newX = initialRect.x + initialRect.width / 2 - newW / 2;
              newY = initialRect.y + initialRect.height / 2 - newH / 2;
          } else {
              // Normal: Anchor is opposite side
              if (handle.includes('w')) {
                  // Anchored Right
                  newX = (initialRect.x + initialRect.width) - newW;
              }
              // 'e' anchor is Left (initialRect.x), which is default newX
              
              if (handle.includes('n')) {
                  // Anchored Bottom
                  newY = (initialRect.y + initialRect.height) - newH;
              }
              // 's' anchor is Top (initialRect.y), which is default newY
          }
      }
      
      // Calculate parent dimensions (find parent early for snapping too)
      let pW = rootW;
      let pH = rootH;
      const rawParent = activeNodes.find(p => p.id === rawNode.parentId);

      // Grid Snapping
      if (snapToGrid) {
          const snapped = SnapManager.snapResizeToGrid({ x: newX, y: newY, width: newW, height: newH }, handle, gridSize, rawParent);
          newX = snapped.x; newY = snapped.y; newW = snapped.width; newH = snapped.height;
      }
      
      if (rawParent) {
        const resolvedParent = resolveNode(rawParent, effectiveVisualBreakpoint);
        const pRect = calculateRect(resolvedParent, rootW, rootH);
        pW = pRect.width;
        pH = pRect.height;
      }

      // Convert to layout props using resolved anchors
      const resolvedNode = resolveNode(rawNode, effectiveVisualBreakpoint);
      const newLayout = calculateLayout(
        { x: newX, y: newY, width: newW, height: newH },
        resolvedNode.anchors,
        pW,
        pH
      );
      
      const finalProps = {
          x: Math.round(newLayout.x),
          y: Math.round(newLayout.y),
          width: Math.round(newLayout.width),
          height: Math.round(newLayout.height)
      };

      if (activeBreakpoint === 'default') {
          updatedNodesMap.set(id, { ...rawNode, ...finalProps });
      } else {
          const currentOverrides = rawNode.props.responsiveOverrides || {};
          const currentBpOverrides = currentOverrides[activeBreakpoint] || {};
          updatedNodesMap.set(id, {
              ...rawNode,
              props: {
                  ...rawNode.props,
                  responsiveOverrides: {
                      ...currentOverrides,
                      [activeBreakpoint]: {
                          ...currentBpOverrides,
                          layout_x: finalProps.x,
                          layout_y: finalProps.y,
                          layout_width: finalProps.width,
                          layout_height: finalProps.height
                      }
                  }
              }
          });
      }
    });

    const newNodes = activeNodes.map(n => updatedNodesMap.has(n.id) ? updatedNodesMap.get(n.id) : n);

    updateNodes(newNodes);
    return true;
  }, [resizingRef, activeNodes, updateNodes, canvasScale, rootW, rootH, settings, activeBreakpoint, visualBreakpoint]);

  const stopResize = useCallback(() => {
    const wasResizing = !!resizingRef.current;
    resizingRef.current = null;
    return wasResizing;
  }, [resizingRef]);

  return { startResize, updateResize, stopResize };
};
